import { useEventListener } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { toBoolean } from "#ce/utils/props";
import { useSelectedTagsMode } from "#ce/components/SpQuantumSelect/composables/useSelectedTagsMode";

/**
 * Keeps selection-field chrome on the Select activator's resolved variant and density.
 * @param {object} props Reactive Select props; reads selectedTags and selectionClearable.
 * @param {{ multiple: import("vue").Ref<boolean>, variant: import("vue").Ref<string>,
 *   density: import("vue").Ref<string>, activatorSlot: import("vue").Ref<HTMLSlotElement|null>,
 *   hasTrailingContent: import("vue").Ref<boolean> }} options Resolved activator appearance.
 * @returns {{ selectedTagsAppearance: import("vue").ComputedRef<object>,
 *   selectedTagsEnabled: import("vue").ComputedRef<boolean>,
 *   selectionFieldEnabled: import("vue").ComputedRef<boolean>,
 *   selectedTagsVariant: import("vue").ComputedRef<string>,
 *   showSelectionTrailing: import("vue").ComputedRef<boolean>,
 *   selectionTrailingSlot: import("vue").ComputedRef<string|undefined> }} Host modifiers and template mode.
 */
export function useSelectedTagsAppearance(props, { multiple, variant, density, activatorSlot, hasTrailingContent }) {
  const customActivator = ref(false);
  function readActivatorContent() {
    customActivator.value = (activatorSlot.value?.assignedElements().length ?? 0) > 0;
  }
  useEventListener(activatorSlot, "slotchange", readActivatorContent);
  watch(activatorSlot, readActivatorContent, { immediate: true, flush: "post" });
  const selectedTagsEnabled = useSelectedTagsMode(props, multiple);
  const selectionFieldEnabled = computed(() => selectedTagsEnabled.value || toBoolean(props.selectionClearable));
  const selectedTagsVariant = computed(() => (selectionFieldEnabled.value ? "plain" : variant.value));
  const showSelectionTrailing = computed(() => selectionFieldEnabled.value && !customActivator.value);
  const selectionTrailingSlot = computed(() =>
    showSelectionTrailing.value && hasTrailingContent.value ? "selection-trailing" : undefined,
  );
  const selectedTagsAppearance = computed(() => {
    const enabled = selectionFieldEnabled.value;
    return {
      "--selected-tags": selectedTagsEnabled.value,
      "--selection-field": enabled,
      "--tags-density-compact": enabled && density.value === "compact",
      "--tags-variant-plain": enabled && variant.value === "plain",
    };
  });

  return {
    selectedTagsAppearance,
    selectedTagsEnabled,
    selectionFieldEnabled,
    selectedTagsVariant,
    showSelectionTrailing,
    selectionTrailingSlot,
  };
}
